import { YStack, XStack, Text, ZStack, ListItem, View } from "tamagui";
import { RiArrowDropRightLine, RiArrowRightSLine, RiAddLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { getFirstText } from "@utils/func/textTool";
import { getColor } from "@utils/func/colorTool";
import Badge from "@components/layout/Badge";
import RadiusCard from "@components/layout/RadiusCard";
import { IdentityContext } from "@utils/store/user/useUser";
import { useNavigate } from "react-router-dom";
import { useSkinV2Page, useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";
import ListContainer from "./ui/ListContainer";
import SectionLabel from "./ui/SectionLabel";

export const FamilyList = ({ families = [] }: { families?: IdentityContext[] }) => {
  const navigate = useNavigate();
  const skinV2 = useSkinV2Page();
  // 淡霧頁：這個區塊標題與「新增」原本是白字（坐在藍帶上），淡底上要改深色
  const haze = useHazePage();

  const addAction = (
    <XStack
      onPress={() => {
        navigate("/account/addAccount");
      }}
      ai="center"
      gap="$1"
      cursor="pointer">
      <RiAddLine color={haze ? v2.hazeAcc : skinV2 ? "#fff" : colors.primary} />
      <Text fontWeight="bold" color={haze ? v2.hazeAcc : skinV2 ? "#fff" : colors.primary} fontSize={skinV2 ? 13 : 14}>
        新增
      </Text>
    </XStack>
  );

  const rows = (
    <>
        {families?.length > 0 ? (
          families.map((item, index) => (
            <ListItem
              key={item.context_id ?? index}
              style={{ background: "#fff" }}
              cursor="pointer"
              onPress={() => navigate(`/account/detail/${item.context_id}`, { state: { context: item } })}
              /* V2 的帳戶字圖：正圓 + 同色系細外框 + 更粗的字。
                 原本是淺色塊配一般粗細的字，在白卡上像一塊沒收邊的色紙；加一圈 color+"26"
                 的邊線把它收住，字級／字重提上來後才像一個「帳戶頭像」而不是背景色塊。

                 形狀用正圓不是圓角方塊：App 裡另外兩處頭像（身分膠囊的縮圖、成員頭像列）
                 都是正圓，這裡做成方的會讓同一種東西在不同頁面長得不一樣。
                 圓形也天然帶著「這代表一個人／一個帳戶」的語彙，方塊比較像分類標籤。 */
              icon={
                <ZStack
                  backgroundColor={getColor(index).bgColor}
                  width={skinV2 ? 38 : 40}
                  height={skinV2 ? 38 : 40}
                  borderRadius={skinV2 ? 999 : "$3"}
                  borderWidth={skinV2 ? 1 : 0}
                  borderColor={skinV2 ? `${getColor(index).color}26` : undefined}
                  flexShrink={0}>
                  <XStack fullscreen alignItems="center" justifyContent="center">
                    <Text color={getColor(index).color} fontWeight={skinV2 ? "800" : "bold"} fontSize={skinV2 ? 15 : undefined}>
                      {getFirstText(item.label || item.context_name || "")}
                    </Text>
                  </XStack>
                </ZStack>
              }
              /* V2 的右箭頭換成細線 chevron（與其他 V2 列一致）；V1 的 RiArrowDropRightLine 是實心三角，
                 和新版的線性圖示語彙不同款。 */
              iconAfter={
                skinV2 ? <RiArrowRightSLine color={v2.sub} size={18} /> : <RiArrowDropRightLine color={colors.grayDark} size={22} />
              }
              title={
                <XStack gap="$1">
                  <Text fontWeight="bold" fontSize={14}>
                    {item.label || item.context_name}
                  </Text>
                  <View alignSelf="flex-start" mx="$2">
                    <Badge>{item?.members?.length || item?.member_count || 0}人</Badge>
                  </View>
                </XStack>
              }
              pressStyle={{ backgroundColor: "$gray3" }}
              subTitle={typeof item.address === "string" ? item.address : item.choose_address?.full_address || ""}
              borderBottomWidth={1}
              borderBottomColor={skinV2 ? v2.divider : "$gray3"}
            />
          ))
        ) : (
          <RadiusCard>
            <Text fontSize={14} color={colors?.gray} textAlign="center">
              尚未建立瓦斯帳戶
            </Text>
          </RadiusCard>
        )}
    </>
  );

  // V2：標題改用與其他區塊一致的 SectionLabel（右側掛「新增」），清單套 ListContainer 的白卡外框
  if (skinV2) {
    return (
      <YStack mt="$3">
        <SectionLabel title="我的瓦斯帳戶" right={addAction} tone={haze ? "dark" : "light"} />
        {families?.length > 0 ? <ListContainer>{rows}</ListContainer> : rows}
      </YStack>
    );
  }

  return (
    <YStack mt="$5" gap="$2.5">
      <XStack jc="space-between" ai="center" px="$1">
        <Text fontWeight="bold" fontSize={14}>
          我的瓦斯帳戶
        </Text>
        {addAction}
      </XStack>
      <YStack ov="hidden" br="$4">
        {rows}
      </YStack>
    </YStack>
  );
};
